/**
 * Template Picker Modal Component
 *
 * Modal for selecting pre-built criteria templates.
 * Templates are grouped by category for easy navigation.
 */

import { useState, useEffect } from 'react';
import { X, FileText, ChevronDown, ChevronRight, Search, AlertCircle } from 'lucide-react';
import type { CriteriaTemplate, CriteriaTemplatesByCategory, CriterionCategory, CriterionType } from '../../types';

interface TemplatePickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTemplate: (template: CriteriaTemplate, category: CriterionCategory) => void;
  templates: CriteriaTemplatesByCategory | null;
  loading: boolean;
  error: string | null;
  criterionType: CriterionType;
}

const CATEGORY_LABELS: Record<string, string> = {
  demographics: 'Demographics',
  medical_history: 'Medical History',
  medications: 'Medications',
  laboratory: 'Laboratory',
  procedures: 'Procedures',
  lifestyle: 'Lifestyle',
  reproductive: 'Reproductive',
  consent: 'Consent',
  other: 'Other',
};

const CATEGORY_ORDER: CriterionCategory[] = [
  'demographics',
  'medical_history',
  'medications',
  'laboratory',
  'procedures',
  'lifestyle',
  'reproductive',
  'consent',
  'other',
];

export function TemplatePickerModal({
  isOpen,
  onClose,
  onSelectTemplate,
  templates,
  loading,
  error,
  criterionType,
}: TemplatePickerModalProps) {
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
      // Expand all categories by default
      if (templates) {
        setExpandedCategories(new Set(Object.keys(templates)));
      }
    }
  }, [isOpen, templates]);

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  // Filter templates by search query
  const filteredTemplates: CriteriaTemplatesByCategory = {};
  if (templates) {
    Object.entries(templates).forEach(([category, categoryTemplates]) => {
      const filtered = categoryTemplates.filter(
        t =>
          t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          t.criterion_text.toLowerCase().includes(searchQuery.toLowerCase())
      );
      if (filtered.length > 0) {
        filteredTemplates[category] = filtered;
      }
    });
  }

  // Sort categories by predefined order
  const sortedCategories = Object.keys(filteredTemplates).sort((a, b) => {
    const aIndex = CATEGORY_ORDER.indexOf(a as CriterionCategory);
    const bIndex = CATEGORY_ORDER.indexOf(b as CriterionCategory);
    return aIndex - bIndex;
  });

  const totalTemplates = Object.values(filteredTemplates).reduce(
    (sum, arr) => sum + arr.length,
    0
  );

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      onKeyDown={handleKeyDown}
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="template-picker-title"
          className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-100 dark:bg-primary-900/40 rounded-lg">
                <FileText size={20} className="text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <h2 id="template-picker-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Select a Template
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Choose a pre-built {criterionType} criterion to customize
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              aria-label="Close modal"
            >
              <X size={20} />
            </button>
          </div>

          {/* Search */}
          <div className="px-6 py-3 border-b border-gray-100 dark:border-gray-700">
            <div className="relative">
              <Search
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500"
              />
              <input
                type="text"
                placeholder="Search templates..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                autoFocus
              />
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                <span className="ml-3 text-gray-600 dark:text-gray-400">Loading templates...</span>
              </div>
            ) : error ? (
              <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
                <p className="text-red-800 dark:text-red-300">{error}</p>
              </div>
            ) : totalTemplates === 0 ? (
              <div className="text-center py-12">
                <FileText size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-3" />
                <p className="text-gray-500 dark:text-gray-400">
                  {searchQuery
                    ? 'No templates match your search'
                    : 'No templates available'}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {sortedCategories.map(category => {
                  const categoryTemplates = filteredTemplates[category];
                  const isExpanded = expandedCategories.has(category);

                  return (
                    <div key={category} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                      {/* Category header */}
                      <button
                        onClick={() => toggleCategory(category)}
                        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-900/40 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left min-h-[44px] sm:min-h-0"
                      >
                        <div className="flex items-center gap-2">
                          {isExpanded ? (
                            <ChevronDown size={18} className="text-gray-500 dark:text-gray-400" />
                          ) : (
                            <ChevronRight size={18} className="text-gray-500 dark:text-gray-400" />
                          )}
                          <span className="font-medium text-gray-900 dark:text-gray-100">
                            {CATEGORY_LABELS[category] || category}
                          </span>
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            ({categoryTemplates.length})
                          </span>
                        </div>
                      </button>

                      {/* Templates */}
                      {isExpanded && (
                        <div className="divide-y divide-gray-100 dark:divide-gray-700">
                          {categoryTemplates.map((template, index) => (
                            <button
                              key={`${category}-${index}`}
                              onClick={() => onSelectTemplate(template, category as CriterionCategory)}
                              className="w-full px-4 py-3 text-left hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors group min-h-[44px] sm:min-h-0"
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-gray-900 dark:text-gray-100 group-hover:text-primary-600">
                                    {template.name}
                                  </p>
                                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                                    {template.criterion_text}
                                  </p>
                                </div>
                                <span className="ml-3 text-xs text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                                  Use
                                </span>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {totalTemplates} template{totalTemplates !== 1 ? 's' : ''} available
              </p>
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors min-h-[44px] sm:min-h-0"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
